import { createAsyncThunk } from '@reduxjs/toolkit';

import { isCurrentVoiceSessionHandedOffSelector } from './voiceSessionHandoffSlice';
import {
  setVoiceSessionStatus,
  voiceSessionIdSelector,
} from './voiceSessionSlice';

import { VoiceMeetingEventBus } from 'features/voice-meeting/services';
import type { RootState } from 'features/app/store/store';
import { isPrimaryVcoSenderSelector } from 'features/vco/store';
import { VoiceSessionStatus } from 'features/voice-session/enums';
import { VoiceSessionDaoService } from 'features/voice-session/services';

type DeleteVoiceSessionThunkOptions = {
  emitCleanupIntent?: boolean;
};

export const deleteVoiceSessionThunk = createAsyncThunk<
  void,
  DeleteVoiceSessionThunkOptions | undefined
>(
  'voiceSession/deleteVoiceSessionThunk',
  async (options, { dispatch, getState }) => {
    const state = getState() as RootState;
    const sessionId = voiceSessionIdSelector(state);
    const isPrimaryVcoSender = isPrimaryVcoSenderSelector(state);
    const isVoiceSessionHandedOff =
      isCurrentVoiceSessionHandedOffSelector(state);
    const { emitCleanupIntent = true } = options ?? {};

    if (!sessionId) {
      return;
    }

    dispatch(setVoiceSessionStatus(VoiceSessionStatus.DISCONNECTING));

    // A handed-off session belongs to someone else now. The handing-off flow
    // detaches the local session itself, so nothing is deleted or reset here.
    if (isPrimaryVcoSender && !isVoiceSessionHandedOff) {
      try {
        await VoiceSessionDaoService.deleteVoiceSession({
          sessionId,
        });
      } catch (error) {
        // If backend cleanup raced or failed, still finish local teardown so the UI
        // does not remain stuck in a disconnecting state.
        console.warn('DEBUG: Error deleting voice session', error);
      } finally {
        if (emitCleanupIntent) {
          VoiceMeetingEventBus.resetLocalSession();
        }
      }
    }
  }
);
